import { pageConfig } from '@/common/service/page-config';
import _ from 'lodash';
import { planTypes } from '@/common/constants';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';
import { partnerReferImageFileMap } from '@/views/config/partner-refer/helper/constant';
import { isEmptyVal } from '@/utils';

/**
 *
 * @return {PartnerReferBean}
 */
export const getPartnerRefer = () => {
  return pageConfig.partnerReferral ?? {};
};

/**
 * @description get all tracking plan names for partner promotion
 * @param planList
 * @param groupByPlanCodePlanMap
 * @return {string}
 */
export const getTrackingPlanNames = (planList, groupByPlanCodePlanMap = {}) => {
  return _.chain(planList)
    .reduce((pre, planCode) => {
      const { type, category } = _.get(groupByPlanCodePlanMap, [planCode], {});

      const planTypeKey = planTypes[type];
      const planName = getTrackingProductNameByPlanType(planTypeKey) ?? type;
      const trackingName = (planName || category) ? `${planName} ${category}` : ''

      return [...pre, trackingName];
    }, [])
    .filter(Boolean)
    .join(',')
    .value();
}

/**
 *
 * @returns {*|{offerDetailLogo: string, offerPreloadFeature: string, planCardLogo: string}}
 */
export const getPartnerImageFileInfo = (partnerReferInfo) => {
  const partnerRefer = partnerReferInfo ?? getPartnerRefer();
  const partnerName = partnerRefer?.partnerName?.toLowerCase();

  return partnerReferImageFileMap?.[partnerName] || {
    planCardLogo: '',
    offerDetailLogo: '',
    offerPreloadFeature: '',
  };
};

export const replaceOfferDetailWithImageLink = (content = '', pattern, imgFileLink = '') => {
  const partnerRefer = getPartnerRefer();

  if (partnerRefer.features?.containDynamicImageFile) {
    return imgFileLink ? _.chain(content)
      .replace(pattern, imgFileLink)
      .value() : content;
  }

  return content;
}

/**
 * @description Create a loading spinner placeholder. Optional custom style.
 * @param {string} style - Optional custom inline style string for the spinner container
 * @returns {string} - HTML string of the loading spinner
 */
export const createLoadingPlaceholder = (
  style = "margin-right: 8px; transform: translateY(3px);"
) => {
  // SVG uses currentColor for flexible theming via surrounding CSS
  return `
    <span class="zbo-spinners is-sm" style="${style}">
      <svg
        class="zbo-spinners__circle"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8 2C4.68629 2 2 4.68629 2 8H0C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8H14C14 4.68629 11.3137 2 8 2Z"
          fill="url(#zm-loading-25)"
        ></path>
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8 14C11.3137 14 14 11.3137 14 8L16 8C16 12.4183 12.4183 16 8 16C3.58172 16 -3.13124e-07 12.4183 -6.99382e-07 8C-7.47664e-07 7.44772 0.447714 7 0.999999 7C1.55228 7 2 7.44772 2 8C2 11.3137 4.68629 14 8 14Z"
          fill="url(#zm-loading-26)"
        ></path>
        <defs>
          <linearGradient id="zbo-loading-25" x1="16" y1="8" x2="0" y2="8" gradientUnits="userSpaceOnUse">
            <stop offset="0.062" stop-color="currentColor" stop-opacity="0.5"></stop>
            <stop offset="1" stop-color="currentColor" stop-opacity="0"></stop>
          </linearGradient>
          <linearGradient id="zbo-loading-26" x1="0" y1="8" x2="16" y2="8" gradientUnits="userSpaceOnUse">
            <stop stop-color="currentColor"></stop>
            <stop offset="0.9375" stop-color="currentColor" stop-opacity="0.5"></stop>
          </linearGradient>
        </defs>
      </svg>
    </span>
  `.replace(/\s+/g, ' ').trim();
};

export const isFromPartnerCampaign = () => {
  return isEmptyVal(window.$config?.('partnerReferral')) === false;
}
